import type { TFunction } from 'i18next';

import { staffLabel } from './staff';
import type { ConversationMessageView } from './types';

/**
 * The timeline body-resolution order (design §7.4/§11.4, evaluated top-down, first match wins):
 * 1. `deleted` → a placeholder, nothing else of the body renders.
 * 2. An inbound row with `text === null` and the session lacks `customers.feature.pii-view` → masked.
 * 3. Otherwise render the kind's own body, with `editedText ?? text` as the text.
 */
export type MessageBodyKind = 'deleted' | 'masked' | 'normal';

export function resolveMessageBodyKind(
  message: ConversationMessageView,
  canViewPii: boolean,
): MessageBodyKind {
  if (message.deleted) {
    return 'deleted';
  }
  if (message.direction === 'IN' && message.text === null && !canViewPii) {
    return 'masked';
  }
  return 'normal';
}

/** The effective text of a `normal` row — the corrected text when edited, the raw text otherwise. */
export function effectiveText(message: ConversationMessageView): string | null {
  return message.editedText ?? message.text ?? null;
}

/**
 * Recognises an operator reply (design §13.3): `direction=OUT && (kind=OPERATOR ||
 * handledBy=OPERATOR)`. The `handledBy` half is required — until the hub's backfill has run, a real
 * operator reply is `kind=TEXT, handledBy=OPERATOR`, and a `kind==='OPERATOR'`-only check renders it
 * as a bot message. Used by `MessageBubble` (surface colour and who-label), `resolveWhoLabel`,
 * `resolveCallbackLabel` (skip operator rows — they are not the bot menu), `QuotedReplyStrip` and the
 * composer's reply-to strip, and Customer 360's compact row list.
 */
export function isOperatorRow(message: ConversationMessageView): boolean {
  return (
    message.direction === 'OUT' && (message.kind === 'OPERATOR' || message.handledBy === 'OPERATOR')
  );
}

/**
 * The operator meta-line label (design §13.2/§13.3) — one of four full, pre-composed strings, never
 * "Operator" concatenated with a raw actor id: "Operator" (no ref at all — a purged/legacy row),
 * "Operator · you", "Operator · {{name}}", "Operator · former employee", or — for `UNAVAILABLE`/a
 * nameless member — plain "Operator" again, this time with a tooltip explaining why no name shows.
 */
export function operatorMetaLabel(
  message: ConversationMessageView,
  meId: string | null | undefined,
  t: TFunction,
): { label: string; tooltip?: string } {
  const result = staffLabel(message.operator, message.operatorActorId, meId, t);
  if (!result) {
    return { label: t('timeline.who.operator') };
  }
  switch (result.kind) {
    case 'you':
      return { label: t('timeline.who.operatorYou') };
    case 'named':
      return { label: t('timeline.who.operatorNamed', { name: result.label }) };
    case 'formerEmployee':
      return { label: t('timeline.who.operatorFormer') };
    default:
      return { label: t('timeline.who.operator'), tooltip: result.tooltip };
  }
}

/**
 * The label of the tapped button for a `CALLBACK` row (design §7.4): the nearest EARLIER bot row's
 * `actions[]` item whose `value` equals the callback's `text`. `rows` is the rendered (ascending)
 * timeline; `index` is the callback row's position in it. Returns `null` when nothing matches — the
 * caller falls back to the raw text in mono.
 */
export function resolveCallbackLabel(
  rows: ConversationMessageView[],
  index: number,
  value: string | null,
): string | null {
  if (!value) {
    return null;
  }
  for (let i = index - 1; i >= 0; i -= 1) {
    const row = rows[i];
    if (row.direction !== 'OUT' || isOperatorRow(row) || row.kind === 'NOTIFICATION') {
      continue;
    }
    const match = row.actions?.find((action) => action.value === value);
    if (match) {
      return match.label;
    }
  }
  return null;
}

/** A one-line preview of a message's body for the quoted-reply strip (design §11.4). */
export function quotedPreview(
  message: ConversationMessageView,
  canViewPii: boolean,
  t: TFunction,
): string {
  const kind = resolveMessageBodyKind(message, canViewPii);
  if (kind === 'deleted') {
    return t(message.direction === 'OUT' ? 'timeline.deletedOutbound' : 'timeline.deleted');
  }
  if (kind === 'masked') {
    return t('pii.messageHidden');
  }
  const text = effectiveText(message);
  if (text) {
    return text;
  }
  const firstAttachment = message.attachments?.[0];
  if (firstAttachment) {
    return `[${t(`attachments.types.${firstAttachment.type}`)}]`;
  }
  return '';
}

/**
 * "Customer" / "Bot · FAQ" / "Operator · {{name}}" / "Notification" — the timeline meta line's who
 * label. `meId` (the caller's own `GET /api/v1/me` `user.id`) decides the "you" variant of an operator
 * row (design §13.2); omit it where the caller is not available (the label still renders, just never
 * as "you").
 */
export function resolveWhoLabel(
  message: ConversationMessageView,
  t: TFunction,
  meId?: string | null,
): string {
  if (message.direction === 'IN') {
    return t('timeline.who.customer');
  }
  if (isOperatorRow(message)) {
    return operatorMetaLabel(message, meId, t).label;
  }
  switch (message.kind) {
    case 'NOTIFICATION':
      return t('timeline.who.notification');
    default:
      switch (message.handledBy) {
        case 'RULE':
          return `${t('timeline.who.bot')} · ${t('timeline.handled.rule')}`;
        case 'COMMAND':
          return `${t('timeline.who.bot')} · ${t('timeline.handled.command')}`;
        case 'FAQ':
          return `${t('timeline.who.bot')} · ${t('timeline.handled.faq')}`;
        case 'AGENT':
          return `${t('timeline.who.bot')} · ${t('timeline.handled.agent', { confidence: message.confidence ?? '' })}`;
        default:
          return t('timeline.who.bot');
      }
  }
}

/** Resolves `replyToMessageId` against the currently loaded rows — undefined when not (yet) loaded. */
export function findQuoted(
  rows: ConversationMessageView[],
  replyToMessageId: string | null | undefined,
): ConversationMessageView | undefined {
  if (!replyToMessageId) {
    return undefined;
  }
  return rows.find((row) => row.id === replyToMessageId);
}
